// components/ExperienciaProfesional.js

import React from 'react';
import {
  Paper,
  Typography,
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Autocomplete
} from '@mui/material';
import { Clear } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import YearChips from '../YearChips';

/**
 * Componente para la sección "4. Experiencia Profesional":
 *  - Año del Draft, Posición en el Draft, Equipo Pre-Draft
 *  - PIT, Eurocamp, SL Vegas
 *  - Experiencia NBA, ACB, Europa, Fuera, NCAA
 */

function ExperienciaProfesional({
  player,
  editablePlayer,
  setEditablePlayer,
  isEditing,
  isMobile,
  teams,            // Array de equipos (para 'Pre-Draft Team')
  handleChange      // Función genérica para onChange
}) {
  const { t } = useTranslation();

  // Para limpiar selects con un botón de Clear
  const clearSelectValue = (e, fieldName) => {
    e.stopPropagation();
    setEditablePlayer((prev) => ({
      ...prev,
      [fieldName]: ''
    }));
  };

  // === Render para MOBILE ===
  if (isMobile) {
    return (
      <Box
        sx={{
          mb: 2,
          backgroundColor: 'background.default'
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 'medium',
            px: 2,
            py: 1,
            backgroundColor: 'background.paper'
          }}
        >
          {"4. " + t('createPlayer.sections.professionalExperience')}
        </Typography>

        <Box sx={{ p: 2 }}>
          {isEditing ? (
            <Grid container spacing={2}>
              {/** Año del Draft, Posición en el Draft, Equipo Pre-Draft */}
              <Grid item xs={12}>
                <TextField
                  label={t('createPlayer.fields.draftYear')}
                  name="añoDraft"
                  type="number"
                  value={editablePlayer.añoDraft || ''}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>{t('createPlayer.fields.draftPosition')}</InputLabel>
                  <Select
                    name="posicionDraft"
                    value={editablePlayer.posicionDraft || ''}
                    onChange={handleChange}
                    label={t('createPlayer.fields.draftPosition')}
                    endAdornment={
                      editablePlayer.posicionDraft && (
                        <IconButton
                          onClick={(e) => clearSelectValue(e, 'posicionDraft')}
                          sx={{ position: 'absolute', right: 32 }}
                          size="small"
                        >
                          <Clear fontSize="small" />
                        </IconButton>
                      )
                    }
                  >
                    {/** 1st round: picks #1 - #30 */}
                    {[...Array(30)].map((_, index) => {
                      const pickNum = index + 1;
                      return (
                        <MenuItem key={pickNum} value={`1st Round, #${pickNum}`}>
                          1st Round, #{pickNum}
                        </MenuItem>
                      );
                    })}

                    {/** 2nd round: picks #31 - #60 */}
                    {[...Array(30)].map((_, index) => {
                      const pickNum = index + 31;
                      return (
                        <MenuItem key={pickNum} value={`2nd Round, #${pickNum}`}>
                          2nd Round, #{pickNum}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  freeSolo
                  options={teams}
                  inputValue={editablePlayer.equipoPreDraft || ''}
                  onInputChange={(event, newInputValue) => {
                    setEditablePlayer((prev) => ({
                      ...prev,
                      equipoPreDraft: newInputValue
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label={t('createPlayer.fields.preDraftTeam')} fullWidth />
                  )}
                />
              </Grid>

              {/** PIT, Eurocamp, SL Vegas */}
              <Grid item xs={12}>
                <TextField
                  label={t('createPlayer.fields.pit')}
                  name="pit"
                  type="number"
                  value={editablePlayer.pit || ''}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <YearChips
                  label={t('createPlayer.fields.eurocamp')}
                  value={editablePlayer.eurocamp || ''}
                  onChange={(newValue) => {
                    setEditablePlayer((prev) => ({
                      ...prev,
                      eurocamp: newValue
                    }));
                  }}
                  helperText={t('createPlayer.fields.yearsHelperText')}
                />
              </Grid>
              <Grid item xs={12}>
                <YearChips
                  label={t('createPlayer.fields.slVegas')}
                  value={editablePlayer.slvegas || ''}
                  onChange={(newValue) => {
                    setEditablePlayer((prev) => ({
                      ...prev,
                      slvegas: newValue
                    }));
                  }}
                  helperText={t('createPlayer.fields.yearsHelperText')}
                />
              </Grid>

              {/** Experiencias: NBA, ACB, Europa, Fuera, NCAA */}
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  {/* Experiencia NBA */}
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>{t('createPlayer.fields.nbaExperience')}</InputLabel>
                      <Select
                        name="nbaExperience"
                        value={editablePlayer.nbaExperience || ''}
                        onChange={handleChange}
                        label={t('createPlayer.fields.nbaExperience')}
                        endAdornment={
                          editablePlayer.nbaExperience && (
                            <IconButton
                              onClick={(e) => clearSelectValue(e, 'nbaExperience')}
                              sx={{ position: 'absolute', right: 32 }}
                              size="small"
                            >
                              <Clear fontSize="small" />
                            </IconButton>
                          )
                        }
                        renderValue={(selected) => t(`common.${selected}`, selected)}
                  >
                        <MenuItem value="yes">{t('common.yes')}</MenuItem>
                        <MenuItem value="no">{t('common.no')}</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Experiencia ACB */}
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>{t('createPlayer.fields.acbExperience')}</InputLabel>
                      <Select
                        name="experienciaAcb"
                        value={editablePlayer.experienciaAcb || ''}
                        onChange={handleChange}
                        label={t('createPlayer.fields.acbExperience')}
                        renderValue={(selected) => t(`common.${selected}`, selected)}
                        endAdornment={
                          editablePlayer.experienciaAcb && (
                            <IconButton
                              onClick={(e) => clearSelectValue(e, 'experienciaAcb')}
                              sx={{ position: 'absolute', right: 32 }}
                              size="small"
                            >
                              <Clear fontSize="small" />
                            </IconButton>
                          )
                        }
                      >
                        <MenuItem value="yes">{t('common.yes')}</MenuItem>
                        <MenuItem value="no">{t('common.no')}</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Experiencia Europa */}
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>{t('createPlayer.fields.europeExperience')}</InputLabel>
                      <Select
                        name="experiencaEuropa"
                        value={editablePlayer.experiencaEuropa || ''}
                        onChange={handleChange}
                        label={t('createPlayer.fields.europeExperience')}
                        renderValue={(selected) => t(`common.${selected}`, selected)}
                        endAdornment={
                          editablePlayer.experiencaEuropa && (
                            <IconButton
                              onClick={(e) => clearSelectValue(e, 'experiencaEuropa')}
                              sx={{ position: 'absolute', right: 32 }}
                              size="small"
                            >
                              <Clear fontSize="small" />
                            </IconButton>
                          )
                        }
                      >
                        <MenuItem value="yes">{t('common.yes')}</MenuItem>
                        <MenuItem value="no">{t('common.no')}</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Experiencia Fuera de su País */}
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>{t('createPlayer.fields.abroadExperience')}</InputLabel>
                      <Select
                        name="experienca_fuera"
                        value={editablePlayer.experienca_fuera || ''}
                        onChange={handleChange}
                        label={t('createPlayer.fields.abroadExperience')}
                        renderValue={(selected) => t(`common.${selected}`, selected)}
                        endAdornment={
                          editablePlayer.experienca_fuera && (
                            <IconButton
                              onClick={(e) => clearSelectValue(e, 'experienca_fuera')}
                              sx={{ position: 'absolute', right: 32 }}
                              size="small"
                            >
                              <Clear fontSize="small" />
                            </IconButton>
                          )
                        }
                      >
                        <MenuItem value="yes">{t('common.yes')}</MenuItem>
                        <MenuItem value="no">{t('common.no')}</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Experiencia NCAA */}
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>{t('createPlayer.fields.ncaaExperience')}</InputLabel>
                      <Select
                        name="experienca_ncaa"
                        value={editablePlayer.experienca_ncaa || ''}
                        onChange={handleChange}
                        label={t('createPlayer.fields.ncaaExperience')}
                        renderValue={(selected) => t(`common.${selected}`, selected)}
                        endAdornment={
                          editablePlayer.experienca_ncaa && (
                            <IconButton
                              onClick={(e) => clearSelectValue(e, 'experienca_ncaa')}
                              sx={{ position: 'absolute', right: 32 }}
                              size="small"
                            >
                              <Clear fontSize="small" />
                            </IconButton>
                          )
                        }
                      >
                        <MenuItem value="yes">{t('common.yes')}</MenuItem>
                        <MenuItem value="no">{t('common.no')}</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          ) : (
            // READ ONLY (MOBILE)
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {(!!player.añoDraft || !!player.posicionDraft || !!player.equipoPreDraft) && (
                <Box>
                  {!!player.añoDraft && (
                    <Typography>
                      <strong>{t('createPlayer.fields.draftYear')}:</strong> {player.añoDraft}
                    </Typography>
                  )}
                  {!!player.posicionDraft && (
                    <Typography>
                      <strong>{t('createPlayer.fields.draftPosition')}:</strong> {player.posicionDraft}
                    </Typography>
                  )}
                  {!!player.equipoPreDraft && (
                    <Typography>
                      <strong>{t('createPlayer.fields.preDraftTeam')}:</strong> {player.equipoPreDraft}
                    </Typography>
                  )}
                </Box>
              )}

              {(!!player.pit || !!player.eurocamp || !!player.slvegas) && (
                <Box>
                  {!!player.pit && (
                    <Typography>
                      <strong>{t('createPlayer.fields.pit')}:</strong> {player.pit}
                    </Typography>
                  )}
                  {!!player.eurocamp && (
                    <Typography>
                      <strong>{t('createPlayer.fields.eurocamp')}:</strong> {player.eurocamp}
                    </Typography>
                  )}
                  {!!player.slvegas && (
                    <Typography>
                      <strong>{t('createPlayer.fields.slVegas')}:</strong> {player.slvegas}
                    </Typography>
                  )}
                </Box>
              )}

              {(!!player.nbaExperience ||
                !!player.experienciaAcb ||
                !!player.experiencaEuropa ||
                !!player.experienca_fuera ||
                !!player.experienca_ncaa) && (
                <Box>
                  {!!player.nbaExperience && (() => {
                    // Support both old Spanish values and new translation keys
                    const yesNoMap = { 'SI': t('common.yes'), 'NO': t('common.no'), 'yes': t('common.yes'), 'no': t('common.no') };
                    const translatedValue = yesNoMap[player.nbaExperience] || player.nbaExperience;
                    return (
                      <Typography>
                        <strong>{t('createPlayer.fields.nbaExperience')}:</strong> {translatedValue}
                      </Typography>
                    );
                  })()}
                  {!!player.experienciaAcb && (() => {
                    const yesNoMap = { 'SI': t('common.yes'), 'NO': t('common.no'), 'yes': t('common.yes'), 'no': t('common.no') };
                    const translatedValue = yesNoMap[player.experienciaAcb] || player.experienciaAcb;
                    return (
                      <Typography>
                        <strong>{t('createPlayer.fields.acbExperience')}:</strong> {translatedValue}
                      </Typography>
                    );
                  })()}
                  {!!player.experiencaEuropa && (() => {
                    const yesNoMap = { 'SI': t('common.yes'), 'NO': t('common.no'), 'yes': t('common.yes'), 'no': t('common.no') };
                    const translatedValue = yesNoMap[player.experiencaEuropa] || player.experiencaEuropa;
                    return (
                      <Typography>
                        <strong>{t('createPlayer.fields.europeExperience')}:</strong> {translatedValue}
                      </Typography>
                    );
                  })()}
                  {!!player.experienca_fuera && (() => {
                    const yesNoMap = { 'SI': t('common.yes'), 'NO': t('common.no'), 'yes': t('common.yes'), 'no': t('common.no') };
                    const translatedValue = yesNoMap[player.experienca_fuera] || player.experienca_fuera;
                    return (
                      <Typography>
                        <strong>{t('createPlayer.fields.abroadExperience')}:</strong> {translatedValue}
                      </Typography>
                    );
                  })()}
                  {!!player.experienca_ncaa && (() => {
                    const yesNoMap = { 'SI': t('common.yes'), 'NO': t('common.no'), 'yes': t('common.yes'), 'no': t('common.no') };
                    const translatedValue = yesNoMap[player.experienca_ncaa] || player.experienca_ncaa;
                    return (
                      <Typography>
                        <strong>{t('createPlayer.fields.ncaaExperience')}:</strong> {translatedValue}
                      </Typography>
                    );
                  })()}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  // === Render para DESKTOP ===
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        mb: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        backgroundColor: 'background.paper',
        boxShadow: '0 1px 3px rgba(16,24,40,0.04)'
      }}
    >
      <Typography
        variant="h6"
        gutterBottom
        sx={{
          fontSize: '1rem',
          fontWeight: 600,
          color: 'text.primary',
          letterSpacing: '-0.01em',
          mb: 1,
          pb: 0.5,
          borderBottom: '1px solid',
          borderColor: 'primary.main'
        }}
      >
        {"4. " + t('createPlayer.sections.professionalExperience')}
      </Typography>

      {isEditing ? (
        <Grid container spacing={2}>
          {/** Año del Draft, Posición en el Draft, Equipo Pre-Draft */}
          <Grid item xs={12} md={4}>
            <TextField
              label={t('createPlayer.fields.draftYear')}
              name="añoDraft"
              type="number"
              value={editablePlayer.añoDraft || ''}
              onChange={handleChange}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>{t('createPlayer.fields.draftPosition')}</InputLabel>
              <Select
                name="posicionDraft"
                value={editablePlayer.posicionDraft || ''}
                onChange={handleChange}
                label={t('createPlayer.fields.draftPosition')}
                endAdornment={
                  editablePlayer.posicionDraft && (
                    <IconButton
                      onClick={(e) => clearSelectValue(e, 'posicionDraft')}
                      sx={{ position: 'absolute', right: 32 }}
                      size="small"
                    >
                      <Clear fontSize="small" />
                    </IconButton>
                  )
                }
              >
                {/** 1st round #1 - #30 */}
                {[...Array(30)].map((_, index) => {
                  const pickNum = index + 1;
                  return (
                    <MenuItem key={pickNum} value={`1st Round, #${pickNum}`}>
                      1st Round, #{pickNum}
                    </MenuItem>
                  );
                })}
                {/** 2nd round #31 - #60 */}
                {[...Array(30)].map((_, index) => {
                  const pickNum = index + 31;
                  return (
                    <MenuItem key={pickNum} value={`2nd Round, #${pickNum}`}>
                      2nd Round, #{pickNum}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <Autocomplete
              freeSolo
              options={teams}
              inputValue={editablePlayer.equipoPreDraft || ''}
              onInputChange={(event, newInputValue) => {
                setEditablePlayer((prev) => ({
                  ...prev,
                  equipoPreDraft: newInputValue
                }));
              }}
              renderInput={(params) => (
                <TextField {...params} label={t('createPlayer.fields.preDraftTeam')} fullWidth />
              )}
            />
          </Grid>

          {/** PIT, Eurocamp, SL Vegas */}
          <Grid item xs={12} md={4}>
            <TextField
              label={t('createPlayer.fields.pit')}
              name="pit"
              type="number"
              value={editablePlayer.pit || ''}
              onChange={handleChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <YearChips
              label={t('createPlayer.fields.eurocamp')}
              value={editablePlayer.eurocamp || ''}
              onChange={(newValue) => {
                setEditablePlayer((prev) => ({
                  ...prev,
                  eurocamp: newValue
                }));
              }}
              helperText={t('createPlayer.fields.yearsHelperText')}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <YearChips
              label={t('createPlayer.fields.slVegas')}
              value={editablePlayer.slvegas || ''}
              onChange={(newValue) => {
                setEditablePlayer((prev) => ({
                  ...prev,
                  slvegas: newValue
                }));
              }}
              helperText={t('createPlayer.fields.yearsHelperText')}
            />
          </Grid>

          {/** Experiencias: NBA, ACB, Europa, Fuera, NCAA */}
          <Grid item xs={12}>
            <Grid container spacing={2}>
              {/* Experiencia NBA */}
              <Grid item xs={12} sm={6} md={2.4}>
                <FormControl fullWidth>
                  <InputLabel>{t('createPlayer.fields.nbaExperience')}</InputLabel>
                  <Select
                    name="nbaExperience"
                    value={editablePlayer.nbaExperience || ''}
                    onChange={handleChange}
                    label={t('createPlayer.fields.nbaExperience')}
                    endAdornment={
                      editablePlayer.nbaExperience && (
                        <IconButton
                          onClick={(e) => clearSelectValue(e, 'nbaExperience')}
                          sx={{ position: 'absolute', right: 32 }}
                          size="small"
                        >
                          <Clear fontSize="small" />
                        </IconButton>
                      )
                    }
                    renderValue={(selected) => t(`common.${selected}`, selected)}
                  >
                    <MenuItem value="yes">{t('common.yes')}</MenuItem>
                    <MenuItem value="no">{t('common.no')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Experiencia ACB */}
              <Grid item xs={12} sm={6} md={2.4}>
                <FormControl fullWidth>
                  <InputLabel>{t('createPlayer.fields.acbExperience')}</InputLabel>
                  <Select
                    name="experienciaAcb"
                    value={editablePlayer.experienciaAcb || ''}
                    onChange={handleChange}
                    label={t('createPlayer.fields.acbExperience')}
                    renderValue={(selected) => t(`common.${selected}`, selected)}
                    endAdornment={
                      editablePlayer.experienciaAcb && (
                        <IconButton
                          onClick={(e) => clearSelectValue(e, 'experienciaAcb')}
                          sx={{ position: 'absolute', right: 32 }}
                          size="small"
                        >
                          <Clear fontSize="small" />
                        </IconButton>
                      )
                    }
                  >
                    <MenuItem value="yes">{t('common.yes')}</MenuItem>
                    <MenuItem value="no">{t('common.no')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Experiencia Europa */}
              <Grid item xs={12} sm={6} md={2.4}>
                <FormControl fullWidth>
                  <InputLabel>{t('createPlayer.fields.europeExperience')}</InputLabel>
                  <Select
                    name="experiencaEuropa"
                    value={editablePlayer.experiencaEuropa || ''}
                    onChange={handleChange}
                    label={t('createPlayer.fields.europeExperience')}
                    renderValue={(selected) => t(`common.${selected}`, selected)}
                    endAdornment={
                      editablePlayer.experiencaEuropa && (
                        <IconButton
                          onClick={(e) => clearSelectValue(e, 'experiencaEuropa')}
                          sx={{ position: 'absolute', right: 32 }}
                          size="small"
                        >
                          <Clear fontSize="small" />
                        </IconButton>
                      )
                    }
                  >
                    <MenuItem value="yes">{t('common.yes')}</MenuItem>
                    <MenuItem value="no">{t('common.no')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Experiencia Fuera de su País */}
              <Grid item xs={12} sm={6} md={2.4}>
                <FormControl fullWidth>
                  <InputLabel>{t('createPlayer.fields.abroadExperience')}</InputLabel>
                  <Select
                    name="experienca_fuera"
                    value={editablePlayer.experienca_fuera || ''}
                    onChange={handleChange}
                    label={t('createPlayer.fields.abroadExperience')}
                    renderValue={(selected) => t(`common.${selected}`, selected)}
                    endAdornment={
                      editablePlayer.experienca_fuera && (
                        <IconButton
                          onClick={(e) => clearSelectValue(e, 'experienca_fuera')}
                          sx={{ position: 'absolute', right: 32 }}
                          size="small"
                        >
                          <Clear fontSize="small" />
                        </IconButton>
                      )
                    }
                  >
                    <MenuItem value="yes">{t('common.yes')}</MenuItem>
                    <MenuItem value="no">{t('common.no')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Experiencia NCAA */}
              <Grid item xs={12} sm={6} md={2.4}>
                <FormControl fullWidth>
                  <InputLabel>{t('createPlayer.fields.ncaaExperience')}</InputLabel>
                  <Select
                    name="experienca_ncaa"
                    value={editablePlayer.experienca_ncaa || ''}
                    onChange={handleChange}
                    label={t('createPlayer.fields.ncaaExperience')}
                    renderValue={(selected) => t(`common.${selected}`, selected)}
                    endAdornment={
                      editablePlayer.experienca_ncaa && (
                        <IconButton
                          onClick={(e) => clearSelectValue(e, 'experienca_ncaa')}
                          sx={{ position: 'absolute', right: 32 }}
                          size="small"
                        >
                          <Clear fontSize="small" />
                        </IconButton>
                      )
                    }
                  >
                    <MenuItem value="yes">{t('common.yes')}</MenuItem>
                    <MenuItem value="no">{t('common.no')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      ) : (
        // READ ONLY (DESKTOP)
        <Box sx={{ mt: 0.5 }}>
          <Grid container rowSpacing={1} columnSpacing={2.5}>
            {!!player.añoDraft && (
              <Grid item xs={12} sm={6} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 1, rowGap: 0.25 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em'
                    }}
                  >
                    {t('createPlayer.fields.draftYear')}:
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.95rem',
                      color: 'text.primary',
                      fontWeight: 400
                    }}
                  >
                    {player.añoDraft}
                  </Typography>
                </Box>
              </Grid>
            )}
          {!!player.posicionDraft && (
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 1, rowGap: 0.25 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em'
                  }}
                >
                  {t('createPlayer.fields.draftPosition')}:
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.95rem',
                    color: 'text.primary',
                    fontWeight: 400
                  }}
                >
                  {player.posicionDraft}
                </Typography>
              </Box>
            </Grid>
          )}
          {!!player.equipoPreDraft && (
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 1, rowGap: 0.25 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em'
                  }}
                >
                  {t('createPlayer.fields.preDraftTeam')}:
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.95rem',
                    color: 'text.primary',
                    fontWeight: 400
                  }}
                >
                  {player.equipoPreDraft}
                </Typography>
              </Box>
            </Grid>
          )}

          {!!player.pit && (
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 1, rowGap: 0.25 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em'
                  }}
                >
                  {t('createPlayer.fields.pit')}:
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.95rem',
                    color: 'text.primary',
                    fontWeight: 400
                  }}
                >
                  {player.pit}
                </Typography>
              </Box>
            </Grid>
          )}
          {!!player.eurocamp && (
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 1, rowGap: 0.25 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em'
                  }}
                >
                  {t('createPlayer.fields.eurocamp')}:
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.95rem',
                    color: 'text.primary',
                    fontWeight: 400
                  }}
                >
                  {player.eurocamp}
                </Typography>
              </Box>
            </Grid>
          )}
          {!!player.slvegas && (
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 1, rowGap: 0.25 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em'
                  }}
                >
                  {t('createPlayer.fields.slVegas')}:
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.95rem',
                    color: 'text.primary',
                    fontWeight: 400
                  }}
                >
                  {player.slvegas}
                </Typography>
              </Box>
            </Grid>
          )}

          {!!player.nbaExperience && (() => {
            // Support both old Spanish values and new translation keys
            const yesNoMap = { 'SI': t('common.yes'), 'NO': t('common.no'), 'yes': t('common.yes'), 'no': t('common.no') };
            const translatedValue = yesNoMap[player.nbaExperience] || player.nbaExperience;
            return (
              <Grid item xs={12} sm={6} md={2.4}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 1, rowGap: 0.25 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em'
                    }}
                  >
                    {t('createPlayer.fields.nbaExperience')}:
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.95rem',
                      color: 'text.primary',
                      fontWeight: 400
                    }}
                  >
                    {translatedValue}
                  </Typography>
                </Box>
              </Grid>
            );
          })()}
          {!!player.experienciaAcb && (() => {
            const yesNoMap = { 'SI': t('common.yes'), 'NO': t('common.no'), 'yes': t('common.yes'), 'no': t('common.no') };
            const translatedValue = yesNoMap[player.experienciaAcb] || player.experienciaAcb;
            return (
              <Grid item xs={12} sm={6} md={2.4}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 1, rowGap: 0.25 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em'
                    }}
                  >
                    {t('createPlayer.fields.acbExperience')}:
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.95rem',
                      color: 'text.primary',
                      fontWeight: 400
                    }}
                  >
                    {translatedValue}
                  </Typography>
                </Box>
              </Grid>
            );
          })()}
          {!!player.experiencaEuropa && (() => {
            const yesNoMap = { 'SI': t('common.yes'), 'NO': t('common.no'), 'yes': t('common.yes'), 'no': t('common.no') };
            const translatedValue = yesNoMap[player.experiencaEuropa] || player.experiencaEuropa;
            return (
              <Grid item xs={12} sm={6} md={2.4}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 1, rowGap: 0.25 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em'
                    }}
                  >
                    {t('createPlayer.fields.europeExperience')}:
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.95rem',
                      color: 'text.primary',
                      fontWeight: 400
                    }}
                  >
                    {translatedValue}
                  </Typography>
                </Box>
              </Grid>
            );
          })()}
          {!!player.experienca_fuera && (() => {
            const yesNoMap = { 'SI': t('common.yes'), 'NO': t('common.no'), 'yes': t('common.yes'), 'no': t('common.no') };
            const translatedValue = yesNoMap[player.experienca_fuera] || player.experienca_fuera;
            return (
              <Grid item xs={12} sm={6} md={2.4}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 1, rowGap: 0.25 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em'
                    }}
                  >
                    {t('createPlayer.fields.abroadExperience')}:
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.95rem',
                      color: 'text.primary',
                      fontWeight: 400
                    }}
                  >
                    {translatedValue}
                  </Typography>
                </Box>
              </Grid>
            );
          })()}
          {!!player.experienca_ncaa && (() => {
            const yesNoMap = { 'SI': t('common.yes'), 'NO': t('common.no'), 'yes': t('common.yes'), 'no': t('common.no') };
            const translatedValue = yesNoMap[player.experienca_ncaa] || player.experienca_ncaa;
            return (
              <Grid item xs={12} sm={6} md={2.4}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 1, rowGap: 0.25 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em'
                    }}
                  >
                    {t('createPlayer.fields.ncaaExperience')}:
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.95rem',
                      color: 'text.primary',
                      fontWeight: 400
                    }}
                  >
                    {translatedValue}
                  </Typography>
                </Box>
              </Grid>
            );
          })()}
          </Grid>
        </Box>
      )}
    </Paper>
  );
}

export default ExperienciaProfesional;
